import { useLingui as _useLingui } from "../lib/i18n/useLingui";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";
const progressBarVariants = cva("h-1.5 overflow-hidden", {
  variants: {
    radius: {
      none: "rounded-none",
      xs: "rounded-xs",
      full: "rounded-full"
    },
    variant: {
      default: "bg-muted-background",
      transparent: "bg-transparent"
    }
  },
  defaultVariants: {
    radius: "full",
    variant: "default"
  }
});
const progressBarFillVariants = cva("h-full", {
  variants: {
    radius: {
      none: "rounded-none",
      xs: "rounded-xs",
      full: "rounded-full"
    },
    variant: {
      default: "bg-primary-light",
      transparent: "bg-primary-light"
    }
  },
  defaultVariants: {
    radius: "full",
    variant: "default"
  }
});
/**
 * A slim determinate progress indicator for a known completion percentage.
 * Exposes the `progressbar` role with value semantics; pass `label` to name
 * what is progressing for screen readers.
 *
 * For indeterminate waits with no percentage, use Spinner instead.
 *
 * @summary Determinate progress indicator (0-100%).
 */
export const ProgressBar = React.forwardRef(({
  percentage,
  values,
  label,
  radius = "full",
  variant = "default",
  className,
  ...props
}, ref) => {
  var _values$map, _normalizedValues$;
  const {
    _: _t
  } = _useLingui();
  const isSegmented = values !== undefined;
  const nonNegativeValues = (_values$map = values === null || values === void 0 ? void 0 : values.map(item => ({
    ...item,
    value: Math.max(0, item.value)
  }))) !== null && _values$map !== void 0 ? _values$map : [];
  const totalValue = nonNegativeValues.reduce((total, item) => total + item.value, 0);
  const normalizedValues = isSegmented ? nonNegativeValues.map(item => ({
    percentage: totalValue > 0 ? item.value / totalValue * 100 : 0,
    className: item.className
  })) : [{
    percentage: Math.min(100, Math.max(0, percentage !== null && percentage !== void 0 ? percentage : 0)),
    className: undefined
  }];
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    role: "progressbar",
    "aria-label": label !== null && label !== void 0 ? label : _t(
    /** i18n */
    {
      id: "CP3D8G",
      message: "Progress"
    }),
    "aria-valuenow": (_normalizedValues$ = normalizedValues[0]) === null || _normalizedValues$ === void 0 ? void 0 : _normalizedValues$.percentage,
    "aria-valuemin": 0,
    "aria-valuemax": 100,
    className: cn(progressBarVariants({
      radius,
      variant
    }), isSegmented && "flex gap-0.5", className),
    ...props
  }, normalizedValues.map((segment, index) => !isSegmented || segment.percentage > 0 ? /*#__PURE__*/React.createElement("div", {
    key: index,
    className: cn(progressBarFillVariants({
      radius,
      variant
    }), segment.className),
    style: isSegmented ? {
      flexBasis: 0,
      flexGrow: segment.percentage
    } : {
      width: `${segment.percentage}%`
    }
  }) : null));
});
ProgressBar.displayName = "ProgressBar";
//# sourceMappingURL=ProgressBar.js.map